import type { ObservationAgreementClause } from '../api/types';

/**
 * The agreement's clauses, laid out as the paper form reads them.
 *
 * It sits here, in neither half of the section, because both halves render the same document: the
 * person signing reads it before they agree, and recruiting reads it back afterwards to see what
 * they agreed to. One renderer means the two cannot drift — which for an agreement is not a tidiness
 * argument but the point of the page, since "what was on screen" is the whole question a signature
 * answers. The same reasoning put `ct-interview/submissionPdf.ts` outside both of its halves.
 *
 * Sharing this does not soften the anonymous/signed-in split `docs/architecture.md` describes: it
 * takes clauses it is given and draws them. It reaches no API, holds no route and knows nothing
 * about tokens.
 */
export function AgreementText({
  clauses,
  namePlaceholder,
  signerName,
}: {
  clauses: ObservationAgreementClause[];

  /**
   * The token a clause carries where the signer's name goes. Omitted on the review side, where the
   * API has already substituted it.
   */
  namePlaceholder?: string;

  /** Blank while nobody has typed a name yet — see `withName`. */
  signerName?: string;
}) {
  return (
    <div className="agreement__body">
      {groupClauses(clauses).map((block) =>
        block.kind === 'list' ? (
          <ul className="agreement__list" key={block.key}>
            {block.clauses.map((clause) => (
              <li key={clause.id}>{withName(clause.text, namePlaceholder, signerName)}</li>
            ))}
          </ul>
        ) : (
          <p
            className={
              block.clause.kind === 'listIntro' ? 'agreement__list-intro' : 'agreement__paragraph'
            }
            key={block.key}
          >
            {withName(block.clause.text, namePlaceholder, signerName)}
          </p>
        ),
      )}
    </div>
  );
}

/**
 * Puts the signer's name in the blank, or an underscore rule while there is nothing to put there.
 *
 * The rule is not decoration: the first sentence is "I, ______, acknowledge and agree as follows",
 * and leaving it to collapse to "I, , acknowledge" would read as a rendering fault rather than as
 * the blank it is. It also makes the name appearing as it is typed the obvious thing that just
 * happened, which is what tells somebody their typed name really is the signature here.
 */
function withName(text: string, namePlaceholder?: string, signerName?: string): string {
  if (!namePlaceholder) {
    return text;
  }

  // split/join rather than replace, so the placeholder is matched literally — it is written with
  // braces, which a regular expression would read as a repetition count.
  return text.split(namePlaceholder).join(signerName && signerName.length > 0 ? signerName : blankRule);
}

/** Non-breaking spaces either side, so the rule never ends up alone at the start of a wrapped line. */
const blankRule = ` ${'_'.repeat(24)} `;

type Block =
  | { kind: 'list'; key: string; clauses: ObservationAgreementClause[] }
  | { kind: 'single'; key: string; clause: ObservationAgreementClause };

/**
 * Runs of consecutive list items become one `<ul>`; everything else stays on its own.
 *
 * The API sends a flat, ordered list because that is what a document is, and what the bullets belong
 * to is decided by what sits next to them. Doing the grouping here rather than sending a tree keeps
 * the stored clause rows flat too, which is what makes "how was this clause worded" a query.
 */
function groupClauses(clauses: ObservationAgreementClause[]): Block[] {
  const blocks: Block[] = [];

  for (const clause of clauses) {
    if (clause.kind !== 'listItem') {
      blocks.push({ kind: 'single', key: clause.id, clause });
      continue;
    }

    const last = blocks.at(-1);

    if (last?.kind === 'list') {
      last.clauses.push(clause);
    } else {
      blocks.push({ kind: 'list', key: clause.id, clauses: [clause] });
    }
  }

  return blocks;
}
